import { useCallback, useEffect, useState } from "react";
import { Loader2, Package, Plus } from "lucide-react";
import { showVatOn } from "@wa/core/sales-vat";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { deleteItem, getItems, type Item } from "../../../lib/sales";
import { useInvoicingVat } from "../../useInvoicingVat";
import {
  Avatar, Btn, EmptyState, MenuField, Modal, PillTabs, SearchField, StatusPill, Toolbar, ToolbarGap, fmtMoney,
  useToast, listCard,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ItemEditModal } from "./ItemEditModal";
import { ItemRowMenu } from "./ItemRowMenu";

/**
 * Name, unit, price, VAT, status - six tracks shared by the head and the rows,
 * so the column heads line up with the figures without either side measuring
 * the other. The price track is the wide one: it is the only number on this
 * screen anybody compares between rows.
 *
 * Which of the two lists is in force is decided once in `ItemsManager` and
 * handed to every row, rather than each row asking again: the head and the rows
 * are one grid, and two of them answering separately is how a column ends up
 * under the wrong heading.
 */
const COLS = "minmax(0,2.6fr) .9fr 1fr .7fr 1fr 44px";

/** The same five tracks with the VAT one dropped, for a catalogue whose items
 *  carry no rate. An empty column would read as a figure that failed to load. */
const COLS_NO_VAT = "minmax(0,2.6fr) .9fr 1fr 1fr 44px";

type StatusTab = "all" | "active" | "inactive";

const STATUS_TABS: StatusTab[] = ["all", "active", "inactive"];

/** What the editor is open on: a blank item, a copy of one, or one being edited. */
type Editor = { mode: "create" | "edit"; item?: Item };

/**
 * Products and services: the saved lines that get dropped into a proposal or an
 * invoice instead of being typed out again.
 *
 * The page renders inside `SalesShell`, which already carries the section head
 * and the sales KPI strip, so this file starts at the toolbar. There is no
 * second KPI row here on purpose - a catalogue has no figures worth a tile that
 * the strip above does not already show.
 */
export function ItemsManager({ user }: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();
  const { vatEnabled } = useInvoicingVat();
  const [items, setItems] = useState<Item[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [group, setGroup] = useState("");
  const [tab, setTab] = useState<StatusTab>("all");
  const [editor, setEditor] = useState<Editor | null>(null);
  const [pending, setPending] = useState<Item | null>(null);
  const [removing, setRemoving] = useState(false);
  const canManage = userCanPlatform(user, "manage:crm.sales");
  // The catalogue decides, not the switch alone: templates saved back when the
  // company charged VAT still carry a rate, and dropping the column would hide
  // the very number the operator has to go and clear.
  const showVat = showVatOn({ vatEnabled, lines: items });
  const cols = showVat ? COLS : COLS_NO_VAT;

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Every filter is answered by the endpoint rather than by trimming the loaded
  // rows: it caps the answer at 500, so a filter applied here would silently
  // mean "of the first 500" and a count that shrinks with it would be a lie.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getItems({
        q: q || undefined,
        active: tab === "all" ? undefined : tab === "active" ? "true" : "false",
        group: group || undefined,
      });
      setItems(res.rows);
      // There is no distinct-groups endpoint, so the group filter learns its
      // options from rows it has already seen and never forgets one. The first
      // load is unfiltered, which is what makes the list complete before the
      // operator can pick anything from it.
      setGroups((prev) => {
        const next = new Set(prev);
        for (const it of res.rows) if (it.group) next.add(it.group);
        return next.size === prev.length ? prev : [...next].sort((a, b) => a.localeCompare(b));
      });
    } finally {
      setLoading(false);
    }
  }, [q, tab, group]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmDelete() {
    if (!pending) return;
    setRemoving(true);
    try {
      await deleteItem(pending.id);
      setItems((p) => p.filter((x) => x.id !== pending.id));
      setPending(null);
    } catch {
      toast(t("common.error"));
    } finally {
      setRemoving(false);
    }
  }

  const filtered = Boolean(q || group || tab !== "all");

  return (
    // One open menu at a time across every row.
    <PopHost>
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("sales.searchItem")} />
        {/* Only offered once at least one item carries a group. A catalogue that
            uses no groups would otherwise get a dropdown whose single entry is
            "all groups", which is a control that does nothing. */}
        {groups.length > 0 ? (
          <MenuField
            value={group}
            onChange={setGroup}
            label={t("sales.items.group")}
            options={[
              { value: "", label: t("sales.items.groupAny") },
              ...groups.map((g) => ({ value: g, label: g })),
            ]}
          />
        ) : null}
        <ToolbarGap />
        <PillTabs
          value={tab}
          onChange={setTab}
          tabs={STATUS_TABS.map((v) => ({
            value: v,
            label: t(v === "all" ? "sales.items.filterAll" : `sales.items.${v}`),
          }))}
        />
        {canManage ? (
          <Btn tone="brand" onClick={() => setEditor({ mode: "create" })}>
            <Plus className="size-4" />
            {t("sales.items.add")}
          </Btn>
        ) : null}
      </Toolbar>

      <div className="crm-card crm-list-stack" style={listCard(cols)}>
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Package}
            title={t("sales.items.empty")}
            body={filtered ? t("sales.items.noMatch") : t("sales.items.emptyBody")}
            action={
              canManage && !filtered ? (
                <Btn tone="brand" onClick={() => setEditor({ mode: "create" })}>
                  <Plus className="size-4" />
                  {t("sales.items.add")}
                </Btn>
              ) : null
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: cols, paddingTop: 22 }}>
              <span>{t("sales.items.name")}</span>
              <span>{t("sales.items.unit")}</span>
              <span>{t("sales.items.price")}</span>
              {showVat ? <span>{t("sales.line.tax")}</span> : null}
              <span>{t("sales.col.status")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {items.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  t={t}
                  cols={cols}
                  showVat={showVat}
                  canManage={canManage}
                  onEdit={() => setEditor({ mode: "edit", item: it })}
                  onDuplicate={() => setEditor({ mode: "create", item: it })}
                  onDelete={() => setPending(it)}
                  onChanged={(next) => setItems((p) => p.map((x) => (x.id === next.id ? { ...x, ...next } : x)))}
                />
              ))}
            </div>

            {/* No pager: the endpoint answers with everything it has, up to 500,
                and there is no page parameter to hand it. The count is the whole
                filtered catalogue rather than a range. */}
            <div className="crm-tfoot">{t("sales.items.count", { n: items.length })}</div>
          </>
        )}
      </div>

      {editor ? (
        <ItemEditModal
          mode={editor.mode}
          item={editor.item}
          onClose={() => setEditor(null)}
          onSaved={() => void load()}
        />
      ) : null}

      {pending ? (
        <Modal
          title={t("sales.items.deleteTitle")}
          label={t("sales.items.deleteTitle")}
          onClose={() => {
            // Not while the delete is in flight: the dialog is the only thing
            // saying the row is on its way out.
            if (!removing) setPending(null);
          }}
        >
          <p className="crm-sub break-words">{t("sales.items.deleteConfirm", { name: pending.name })}</p>
          <div className="crm-m-foot">
            <Btn onClick={() => setPending(null)} disabled={removing}>
              {t("common.cancel")}
            </Btn>
            <Btn tone="danger" onClick={() => void confirmDelete()} disabled={removing}>
              {removing ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("common.delete")}
            </Btn>
          </div>
        </Modal>
      ) : null}
    </PopHost>
  );
}

function ItemRow({
  item,
  t,
  cols,
  showVat,
  canManage,
  onEdit,
  onDuplicate,
  onDelete,
  onChanged,
}: {
  item: Item;
  t: ReturnType<typeof useT>;
  /** The head's own track list, handed down so the two cannot drift apart. */
  cols: string;
  showVat: boolean;
  canManage: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onChanged: (next: Item) => void;
}) {
  // SKU and group both identify the line without repeating its name, and most
  // catalogues carry one of the two rather than both.
  const sub = [item.sku, item.group].filter(Boolean).join(" / ") || "-";

  return (
    // A div, not a link: an item has no page of its own to open, and the row
    // ends in a menu button, which cannot live inside an <a>. The name is a real
    // button stretching an ::after over the whole row, so the row opens the
    // editor from the keyboard as well as from anywhere along its width.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: cols }}>
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={item.name} />
        <span className="min-w-0">
          {canManage ? (
            <button
              type="button"
              onClick={onEdit}
              className="crm-prow-n crm-trunc block w-full text-left after:absolute after:inset-0 after:rounded-full"
            >
              {item.name}
            </button>
          ) : (
            <span className="crm-prow-n crm-trunc block">{item.name}</span>
          )}
          <span className="crm-prow-s crm-trunc block">{sub}</span>
        </span>
      </span>

      <span className="crm-mut text-[12.5px]">{item.unit ?? "-"}</span>
      {/* Exact cents: this is the price that gets typed onto a document, and a
          unit price rounded to the euro would be the wrong number to copy. */}
      <span className="crm-tnum text-[13.25px] font-bold">{fmtMoney(item.unitPriceCents, true)}</span>
      {showVat ? <span className="crm-mut crm-tnum text-[12.5px]">{item.taxRatePct}%</span> : null}
      <span>
        <StatusPill tone={item.active ? "ok" : "mut"}>
          {t(item.active ? "sales.items.active" : "sales.items.inactive")}
        </StatusPill>
      </span>

      {canManage ? (
        <ItemRowMenu
          item={item}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onChanged={onChanged}
        />
      ) : (
        // The track still has to exist, or the head and the row disagree about
        // how many columns there are and every heading slides one to the right.
        <span />
      )}
    </div>
  );
}
